import { useCallback, useLayoutEffect, useRef, memo, useState } from 'react'

import { useTheme } from '@chakra-ui/react'

import { countLines } from 'src/utils/text'
import { PARAGRAPH_LINE_HEIGHT_PROPORTION, getTranscriptDimensions } from 'src/components/constants'
import { isChromakeyModeActive } from 'src/utils/env'
import { useResponsiveContainer } from 'src/contexts/ResponsiveContainerContext'
import { useConfig } from 'src/contexts/ConfigurationContext'
import { SessionDetails } from 'src/components/SessionDetails/SessionDetails'
import { useVerbitConnectAvailability } from 'src/hooks/useVerbitConnectAvailability'
import { TranscriptParagraph } from 'src/models/Transcript'
import { ParagraphTimestamp } from 'src/components/TranscriptViewer/ParagraphTimestamp'
import { ParagraphAuthor } from 'src/components/TranscriptViewer/ParagraphAuthor'
import { ParagraphTextEditable, ParagraphTextReadonly } from 'src/components/TranscriptViewer/ParagraphText'
import { useTranscriptDimensions } from 'src/hooks/useTranscriptDimensions'
import {
    useTranscriptParagraph,
    useTranscriptSpeaker,
    useTranscriptUserStateEvent,
    useTranscriptUserStateManager,
} from 'src/contexts/Transcript/TranscriptContext'
import { useTranscriptMetadata } from 'src/contexts/Transcript/MetadataContext'
import { getSessionTitle } from 'src/models/TranscriptMetadata'
import { InconsistencyIndicators } from 'src/components/TranscriptViewer/InconsistencyIndicators'
import { useInconsistencySelection } from 'src/contexts/InlineInconsistenciesContext'

import * as S from './styles'
import { useParagraphInconsistencies } from './useParagraphInconsistencies'
import { useInconsistencyGutterCss } from './useInconsistencyGutter'
import {
    PARAGRAPH_CONTAINER_DESKTOP_PADDING,
    PARAGRAPH_CONTAINER_MARGIN,
    PARAGRAPH_CONTAINER_MOBILE_PADDING,
    PARAGRAPH_METADATA_DESKTOP_FONT_SIZE_COEFF,
    PARAGRAPH_METADATA_DESKTOP_MARGIN,
    PARAGRAPH_METADATA_LINE_HEIGHT_COEFF,
    PARAGRAPH_METADATA_MOBILE_FONT_SIZE_COEFF,
    PARAGRAPH_METADATA_MOBILE_MARGIN,
    PARAGRAPH_SPEAKER_NAME_DESKTOP_PADDING,
    PARAGRAPH_SPEAKER_NAME_MOBILE_PADDING,
} from './constants'

type ParagraphViewProps = {
    index: number
    paragraphId: string
    containerWidth: number
    isDiarizationSupported: boolean
    ref?: (node: HTMLDivElement | null) => void
    isFirstParagraph: boolean
    previousParagraphId?: string
}

export const ParagraphView = memo((props: ParagraphViewProps) => {
    const { index, paragraphId, containerWidth, isDiarizationSupported, ref, isFirstParagraph, previousParagraphId } = props
    const transcriptMetadata = useTranscriptMetadata()

    const {
        fontFamily,
        textColorIndex,
        textBackgroundColorIndex,
        palette: {
            transcript: { textColors, textBackgroundColors },
        },
    } = useTheme()
    const textColor = textColors[textColorIndex]
    const textBgColor = textBackgroundColors[textBackgroundColorIndex]
    const [{ shouldDisplayLogo, isTimestampFormatsEnabled }] = useConfig()

    const { isSmallContainer } = useResponsiveContainer()
    const { isMediaRecorderAvailable } = useVerbitConnectAvailability()

    const isTimestampVisible = !isChromakeyModeActive && isTimestampFormatsEnabled && transcriptMetadata?.viewMode !== 'document'
    const { transcriptParagraphWidth, metadataWidth } = useTranscriptDimensions(containerWidth)

    const paragraph = useTranscriptParagraph(paragraphId)
    const author = useTranscriptSpeaker(paragraph?.speakerId ?? '')
    const previousParagraph = useTranscriptParagraph(previousParagraphId ?? '')
    const inconsistencies = useParagraphInconsistencies(paragraph, previousParagraph)
    const hasInconsistencies = !isChromakeyModeActive && inconsistencies.length > 0
    const inconsistencyGutter = useInconsistencyGutterCss(containerWidth)
    const { selectedUuid, select } = useInconsistencySelection()
    const selectInconsistency = useCallback(
        (uuid: string) =>
            select(uuid, { position: inconsistencies.findIndex((item) => item.uuid === uuid) + 1, total: inconsistencies.length }),
        [select, inconsistencies],
    )

    const wrapperRef = useRef<HTMLDivElement | null>(null)
    const mergedRef = useCallback(
        (node: HTMLDivElement | null) => {
            wrapperRef.current = node
            ref?.(node)
        },
        [ref],
    )

    // force the virtualizer to re-measure this element synchronously after content
    // changes, before the browser paints;
    // without this, the virtualizer relies on `ResizeObserver` which fires
    // asynchronously (after layout, before paint in the NEXT rendering opportunity),
    // leaving a 1-frame gap where ScrollingContainer height is stale -
    // causing visible flicker when the user is scrolled to the bottom
    useLayoutEffect(() => {
        if (wrapperRef.current) {
            ref?.(wrapperRef.current)
        }
    }, [paragraph, ref])

    const textElementRef = useRef<HTMLDivElement | null>(null)

    const isParagraphCurrentlyEdited = useIsParagraphCurrentlyEdited(paragraphId)
    const ParagraphText = isParagraphCurrentlyEdited ? ParagraphTextEditable : ParagraphTextReadonly

    return (
        <S.Wrapper ref={mergedRef} data-index={index}>
            <S.Container
                $fontFamily={fontFamily}
                $isFirst={isFirstParagraph}
                $shouldShift={shouldDisplayLogo}
                $maxWidth={transcriptParagraphWidth}
                $inconsistencyGutter={inconsistencyGutter}
                data-group={hasInconsistencies || undefined}>
                {isFirstParagraph && (
                    <>
                        {isMediaRecorderAvailable && <SessionDetails maxWidth={transcriptParagraphWidth} />}
                        {isSmallContainer && <S.SessionTitle>{getSessionTitle(transcriptMetadata)}</S.SessionTitle>}
                    </>
                )}
                <S.Metadata $metadataWidth={metadataWidth}>
                    {isTimestampVisible && (
                        <ParagraphTimestamp timeAbsolute={paragraph.timestampAbsolute} timeRelative={paragraph.timestampRelative} />
                    )}
                    <ParagraphAuthor paragraph={paragraph} author={author} isDiarizationSupported={isDiarizationSupported} />
                    {hasInconsistencies && (
                        <S.InconsistenciesSlot
                            className={S.INCONSISTENCIES_CLASSNAME}
                            // `KEEPS_TRANSCRIPT_HIGHLIGHT_ATTRIBUTE`: a press here does not dismiss the highlight.
                            data-keeps-transcript-highlight>
                            <InconsistencyIndicators
                                inconsistencies={inconsistencies}
                                selectedUuid={selectedUuid}
                                onSelect={selectInconsistency}
                            />
                        </S.InconsistenciesSlot>
                    )}
                </S.Metadata>

                <ParagraphText
                    textElementRef={textElementRef}
                    color={textColor.colorValue}
                    backgroundColor={textBgColor.colorValue}
                    paragraph={paragraph}
                />
            </S.Container>
        </S.Wrapper>
    )
})

const useIsParagraphCurrentlyEdited = (paragraphId: string) => {
    const userStateManager = useTranscriptUserStateManager()
    const [isEdited, setIsEdited] = useState(() => userStateManager.getLocalState().selection?.paragraphId === paragraphId)

    useTranscriptUserStateEvent((localState) => {
        const newIsEdited = localState.selection?.paragraphId === paragraphId

        if (newIsEdited !== isEdited) {
            setIsEdited(newIsEdited)
        }
    })

    return isEdited
}

type GetMetadataHeightDto = { fontSize: number; isMobileLayout: boolean }
export const getMetadataHeight = ({ fontSize, isMobileLayout }: GetMetadataHeightDto) => {
    const fontSizeCoeff = isMobileLayout ? PARAGRAPH_METADATA_MOBILE_FONT_SIZE_COEFF : PARAGRAPH_METADATA_DESKTOP_FONT_SIZE_COEFF
    let metadataHeight = fontSize * fontSizeCoeff
    if (!isMobileLayout) {
        metadataHeight *= PARAGRAPH_METADATA_LINE_HEIGHT_COEFF
    }

    const margin = isMobileLayout ? PARAGRAPH_METADATA_MOBILE_MARGIN : PARAGRAPH_METADATA_DESKTOP_MARGIN
    const padding = isMobileLayout ? PARAGRAPH_SPEAKER_NAME_MOBILE_PADDING : PARAGRAPH_SPEAKER_NAME_DESKTOP_PADDING

    return metadataHeight + margin + padding * 2
}

type GetParagraphMaxWidthDto = {
    isMobilePhone: boolean
    isFullWidthMode: boolean
    containerWidth: number
    shouldDisplayLogo: boolean
    // Extra left padding that makes room for the inconsistency icon (`useInconsistencyGutter`).
    inconsistencyGutter?: number
}

/**
 * Calculates the maximum width of the paragraph container
 *
 * On mobile, it's full device width minus paddings.
 * On desktop, it depends on the view mode (full screen vs limited width),
 * green chromakey mode (different layout) and presence of the logo -
 * check `getTranscriptDimensions` for more details.
 */
export const getParagraphMaxWidth = ({
    isMobilePhone,
    containerWidth,
    isFullWidthMode,
    shouldDisplayLogo,
    inconsistencyGutter = 0,
}: GetParagraphMaxWidthDto) => {
    if (isMobilePhone) {
        return containerWidth - PARAGRAPH_CONTAINER_MOBILE_PADDING * 2 - PARAGRAPH_CONTAINER_MARGIN * 2 - inconsistencyGutter
    }

    const { transcriptParagraphWidth, metadataWidth } = getTranscriptDimensions({
        isFullWidthMode,
        containerWidth,
        shouldDisplayLogo,
    })
    let paragraphMaxWidth = Math.min(transcriptParagraphWidth, containerWidth)
    paragraphMaxWidth -= PARAGRAPH_CONTAINER_DESKTOP_PADDING * 2 + inconsistencyGutter
    if (isChromakeyModeActive) {
        paragraphMaxWidth -= metadataWidth
    }

    return paragraphMaxWidth
}

type GetContentHeightDto = { linesAmount: number; fontSize: number }

export const getContentHeight = ({ linesAmount, fontSize }: GetContentHeightDto) =>
    linesAmount * (fontSize * PARAGRAPH_LINE_HEIGHT_PROPORTION)

type GetParagraphHeightDto = {
    paragraph: TranscriptParagraph
    maxWidth: number
    metadataHeight: number
    fontSize: number
    isMobileLayout: boolean
}

export const getParagraphHeight = ({ paragraph, maxWidth, metadataHeight, fontSize, isMobileLayout }: GetParagraphHeightDto) => {
    const linesAmount = Math.max(1, countLines({ content: paragraph.content, maxWidth, fontSize }))
    const contentHeight = getContentHeight({ linesAmount, fontSize })
    let totalHeight = contentHeight + metadataHeight

    const padding = isMobileLayout ? PARAGRAPH_CONTAINER_MOBILE_PADDING : PARAGRAPH_CONTAINER_DESKTOP_PADDING
    if (isChromakeyModeActive) {
        totalHeight += padding
    } else {
        totalHeight += padding * 2
    }

    return totalHeight
}
